/**
 * Mock workouts data for development.
 * Includes 3 months of workout history spread across 90+ days for realistic
 * analytics charts, plus workout templates.
 */
import { subDays, subHours } from 'date-fns';
import { MOCK_CURRENT_USER_ID } from './mock-users.data';

const now = new Date();

const makeWorkout = (overrides) => ({
  id: overrides.id || `mock_wkt_${Date.now().toString(36)}`,
  userId: MOCK_CURRENT_USER_ID,
  title: 'Workout',
  workoutType: 'strength',
  exercises: [],
  duration: 60,
  caloriesBurned: 300,
  notes: '',
  sharedAsPostId: null,
  templateId: null,
  isTemplate: false,
  completedAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  _docSnapshot: null,
  ...overrides,
});

export const MOCK_WORKOUTS = [
  // ── Week 1 (This week) ──────────────────────────────────────────────────
  // Today
  makeWorkout({
    id: 'mock_wkt_01',
    title: 'Push Day - Chest & Shoulders',
    workoutType: 'strength',
    duration: 65,
    caloriesBurned: 420,
    completedAt: subHours(now, 4).toISOString(),
    createdAt: subHours(now, 4).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 10, weight: 185, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }, { reps: 6, weight: 205, unit: 'lbs' }] },
      { name: 'Overhead Press', sets: [{ reps: 10, weight: 95, unit: 'lbs' }, { reps: 8, weight: 105, unit: 'lbs' }, { reps: 8, weight: 105, unit: 'lbs' }] },
      { name: 'Incline Dumbbell Press', sets: [{ reps: 12, weight: 60, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }] },
      { name: 'Lateral Raise', sets: [{ reps: 15, weight: 20, unit: 'lbs' }, { reps: 15, weight: 20, unit: 'lbs' }, { reps: 12, weight: 25, unit: 'lbs' }] },
      { name: 'Tricep Pushdown', sets: [{ reps: 15, weight: 50, unit: 'lbs' }, { reps: 12, weight: 55, unit: 'lbs' }, { reps: 12, weight: 55, unit: 'lbs' }] },
    ],
  }),
  // Yesterday
  makeWorkout({
    id: 'mock_wkt_02',
    title: 'Pull Day - Back & Biceps',
    workoutType: 'strength',
    duration: 58,
    caloriesBurned: 390,
    completedAt: subDays(now, 1).toISOString(),
    createdAt: subDays(now, 1).toISOString(),
    exercises: [
      { name: 'Deadlift', sets: [{ reps: 5, weight: 315, unit: 'lbs' }, { reps: 5, weight: 335, unit: 'lbs' }, { reps: 3, weight: 365, unit: 'lbs' }] },
      { name: 'Lat Pulldown', sets: [{ reps: 12, weight: 140, unit: 'lbs' }, { reps: 10, weight: 150, unit: 'lbs' }, { reps: 10, weight: 150, unit: 'lbs' }] },
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 155, unit: 'lbs' }, { reps: 8, weight: 165, unit: 'lbs' }, { reps: 8, weight: 165, unit: 'lbs' }] },
      { name: 'Barbell Curl', sets: [{ reps: 12, weight: 65, unit: 'lbs' }, { reps: 10, weight: 75, unit: 'lbs' }] },
      { name: 'Face Pull', sets: [{ reps: 15, weight: 40, unit: 'lbs' }, { reps: 15, weight: 40, unit: 'lbs' }] },
    ],
  }),
  // 2 days ago
  makeWorkout({
    id: 'mock_wkt_03',
    title: 'Leg Day - Quads & Glutes',
    workoutType: 'strength',
    duration: 72,
    caloriesBurned: 510,
    completedAt: subDays(now, 2).toISOString(),
    createdAt: subDays(now, 2).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 225, unit: 'lbs' }, { reps: 5, weight: 245, unit: 'lbs' }, { reps: 5, weight: 265, unit: 'lbs' }, { reps: 5, weight: 275, unit: 'lbs' }] },
      { name: 'Romanian Deadlift', sets: [{ reps: 10, weight: 185, unit: 'lbs' }, { reps: 10, weight: 185, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }] },
      { name: 'Leg Press', sets: [{ reps: 12, weight: 360, unit: 'lbs' }, { reps: 10, weight: 400, unit: 'lbs' }, { reps: 10, weight: 400, unit: 'lbs' }] },
      { name: 'Walking Lunge', sets: [{ reps: 12, weight: 50, unit: 'lbs' }, { reps: 12, weight: 50, unit: 'lbs' }] },
    ],
  }),
  // 4 days ago
  makeWorkout({
    id: 'mock_wkt_04',
    title: 'Upper Body Push',
    workoutType: 'strength',
    duration: 55,
    caloriesBurned: 360,
    completedAt: subDays(now, 4).toISOString(),
    createdAt: subDays(now, 4).toISOString(),
    exercises: [
      { name: 'Dumbbell Bench Press', sets: [{ reps: 10, weight: 70, unit: 'lbs' }, { reps: 10, weight: 75, unit: 'lbs' }, { reps: 8, weight: 80, unit: 'lbs' }] },
      { name: 'Cable Crossover', sets: [{ reps: 12, weight: 30, unit: 'lbs' }, { reps: 12, weight: 30, unit: 'lbs' }, { reps: 12, weight: 30, unit: 'lbs' }] },
      { name: 'Arnold Press', sets: [{ reps: 10, weight: 40, unit: 'lbs' }, { reps: 10, weight: 45, unit: 'lbs' }] },
      { name: 'Skull Crushers', sets: [{ reps: 12, weight: 55, unit: 'lbs' }, { reps: 10, weight: 60, unit: 'lbs' }] },
    ],
  }),
  // 5 days ago
  makeWorkout({
    id: 'mock_wkt_05',
    title: 'Morning Cardio',
    workoutType: 'cardio',
    duration: 40,
    caloriesBurned: 380,
    completedAt: subDays(now, 5).toISOString(),
    createdAt: subDays(now, 5).toISOString(),
    exercises: [
      { name: 'Running', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      { name: 'Stair Climber', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),
  // 6 days ago
  makeWorkout({
    id: 'mock_wkt_06',
    title: 'Upper Body Pull',
    workoutType: 'strength',
    duration: 50,
    caloriesBurned: 340,
    completedAt: subDays(now, 6).toISOString(),
    createdAt: subDays(now, 6).toISOString(),
    exercises: [
      { name: 'Pull-Up', sets: [{ reps: 10, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }, { reps: 6, weight: 0, unit: 'lbs' }] },
      { name: 'T-Bar Row', sets: [{ reps: 10, weight: 135, unit: 'lbs' }, { reps: 10, weight: 135, unit: 'lbs' }] },
      { name: 'Hammer Curl', sets: [{ reps: 12, weight: 30, unit: 'lbs' }, { reps: 12, weight: 35, unit: 'lbs' }] },
    ],
  }),

  // ── Week 2 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_07',
    title: 'Leg Day - Hamstrings',
    workoutType: 'strength',
    duration: 62,
    caloriesBurned: 440,
    completedAt: subDays(now, 8).toISOString(),
    createdAt: subDays(now, 8).toISOString(),
    exercises: [
      { name: 'Sumo Deadlift', sets: [{ reps: 5, weight: 275, unit: 'lbs' }, { reps: 5, weight: 295, unit: 'lbs' }, { reps: 5, weight: 315, unit: 'lbs' }] },
      { name: 'Leg Curl', sets: [{ reps: 12, weight: 100, unit: 'lbs' }, { reps: 12, weight: 110, unit: 'lbs' }] },
      { name: 'Hip Thrust', sets: [{ reps: 12, weight: 185, unit: 'lbs' }, { reps: 12, weight: 185, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_08',
    title: 'HIIT Sprint Intervals',
    workoutType: 'hiit',
    duration: 30,
    caloriesBurned: 350,
    completedAt: subDays(now, 9).toISOString(),
    createdAt: subDays(now, 9).toISOString(),
    exercises: [
      { name: 'Sprints', sets: [{ reps: 10, weight: 0, unit: 'lbs' }] },
      { name: 'Walking', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_09',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 60,
    caloriesBurned: 400,
    completedAt: subDays(now, 11).toISOString(),
    createdAt: subDays(now, 11).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 185, unit: 'lbs' }, { reps: 8, weight: 185, unit: 'lbs' }, { reps: 6, weight: 195, unit: 'lbs' }] },
      { name: 'Dumbbell Shoulder Press', sets: [{ reps: 10, weight: 50, unit: 'lbs' }, { reps: 10, weight: 55, unit: 'lbs' }] },
      { name: 'Lateral Raise', sets: [{ reps: 15, weight: 20, unit: 'lbs' }, { reps: 15, weight: 20, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_10',
    title: 'Yoga & Mobility',
    workoutType: 'flexibility',
    duration: 50,
    caloriesBurned: 180,
    completedAt: subDays(now, 13).toISOString(),
    createdAt: subDays(now, 13).toISOString(),
    exercises: [
      { name: 'Yoga Flow', sets: [{ reps: 8, weight: 0, unit: 'lbs' }] },
      { name: 'Static Stretching', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),

  // ── Week 3 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_11',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 55,
    caloriesBurned: 370,
    completedAt: subDays(now, 15).toISOString(),
    createdAt: subDays(now, 15).toISOString(),
    exercises: [
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 145, unit: 'lbs' }, { reps: 8, weight: 155, unit: 'lbs' }] },
      { name: 'Chin-Up', sets: [{ reps: 10, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }] },
      { name: 'Dumbbell Curl', sets: [{ reps: 12, weight: 30, unit: 'lbs' }, { reps: 10, weight: 35, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_12',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 68,
    caloriesBurned: 490,
    completedAt: subDays(now, 18).toISOString(),
    createdAt: subDays(now, 18).toISOString(),
    exercises: [
      { name: 'Front Squat', sets: [{ reps: 8, weight: 185, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }, { reps: 6, weight: 205, unit: 'lbs' }] },
      { name: 'Bulgarian Split Squat', sets: [{ reps: 10, weight: 40, unit: 'lbs' }, { reps: 10, weight: 40, unit: 'lbs' }] },
      { name: 'Leg Extension', sets: [{ reps: 15, weight: 100, unit: 'lbs' }, { reps: 12, weight: 110, unit: 'lbs' }] },
      { name: 'Calf Raise', sets: [{ reps: 20, weight: 150, unit: 'lbs' }, { reps: 20, weight: 150, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_13',
    title: 'Morning Run',
    workoutType: 'cardio',
    duration: 35,
    caloriesBurned: 320,
    completedAt: subDays(now, 20).toISOString(),
    createdAt: subDays(now, 20).toISOString(),
    exercises: [
      { name: 'Running', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),

  // ── Week 4 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_14',
    title: 'Full Body',
    workoutType: 'strength',
    duration: 70,
    caloriesBurned: 460,
    completedAt: subDays(now, 22).toISOString(),
    createdAt: subDays(now, 22).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 225, unit: 'lbs' }, { reps: 5, weight: 225, unit: 'lbs' }] },
      { name: 'Bench Press', sets: [{ reps: 5, weight: 185, unit: 'lbs' }, { reps: 5, weight: 185, unit: 'lbs' }] },
      { name: 'Barbell Row', sets: [{ reps: 5, weight: 155, unit: 'lbs' }, { reps: 5, weight: 155, unit: 'lbs' }] },
      { name: 'Plank', sets: [{ reps: 3, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_15',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 58,
    caloriesBurned: 380,
    completedAt: subDays(now, 25).toISOString(),
    createdAt: subDays(now, 25).toISOString(),
    exercises: [
      { name: 'Overhead Press', sets: [{ reps: 8, weight: 95, unit: 'lbs' }, { reps: 8, weight: 100, unit: 'lbs' }] },
      { name: 'Bench Press', sets: [{ reps: 8, weight: 175, unit: 'lbs' }, { reps: 8, weight: 180, unit: 'lbs' }] },
      { name: 'Tricep Pushdown', sets: [{ reps: 15, weight: 45, unit: 'lbs' }, { reps: 12, weight: 50, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_16',
    title: 'Cardio & Core',
    workoutType: 'cardio',
    duration: 45,
    caloriesBurned: 400,
    completedAt: subDays(now, 28).toISOString(),
    createdAt: subDays(now, 28).toISOString(),
    exercises: [
      { name: 'Rowing Machine', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      { name: 'Plank', sets: [{ reps: 3, weight: 0, unit: 'lbs' }] },
      { name: 'Russian Twist', sets: [{ reps: 20, weight: 25, unit: 'lbs' }, { reps: 20, weight: 25, unit: 'lbs' }] },
    ],
  }),

  // ── Week 5 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_17',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 54,
    caloriesBurned: 365,
    completedAt: subDays(now, 30).toISOString(),
    createdAt: subDays(now, 30).toISOString(),
    exercises: [
      { name: 'Deadlift', sets: [{ reps: 5, weight: 295, unit: 'lbs' }, { reps: 5, weight: 315, unit: 'lbs' }, { reps: 3, weight: 345, unit: 'lbs' }] },
      { name: 'Seated Cable Row', sets: [{ reps: 12, weight: 130, unit: 'lbs' }, { reps: 10, weight: 140, unit: 'lbs' }] },
      { name: 'Barbell Curl', sets: [{ reps: 12, weight: 60, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_18',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 66,
    caloriesBurned: 470,
    completedAt: subDays(now, 32).toISOString(),
    createdAt: subDays(now, 32).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 215, unit: 'lbs' }, { reps: 5, weight: 235, unit: 'lbs' }, { reps: 5, weight: 255, unit: 'lbs' }] },
      { name: 'Leg Press', sets: [{ reps: 12, weight: 340, unit: 'lbs' }, { reps: 10, weight: 380, unit: 'lbs' }] },
      { name: 'Leg Curl', sets: [{ reps: 12, weight: 90, unit: 'lbs' }, { reps: 12, weight: 100, unit: 'lbs' }] },
      { name: 'Calf Raise', sets: [{ reps: 20, weight: 140, unit: 'lbs' }, { reps: 20, weight: 140, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_19',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 60,
    caloriesBurned: 400,
    completedAt: subDays(now, 34).toISOString(),
    createdAt: subDays(now, 34).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 175, unit: 'lbs' }, { reps: 8, weight: 175, unit: 'lbs' }, { reps: 6, weight: 185, unit: 'lbs' }] },
      { name: 'Incline Dumbbell Press', sets: [{ reps: 10, weight: 55, unit: 'lbs' }, { reps: 10, weight: 60, unit: 'lbs' }] },
      { name: 'Overhead Press', sets: [{ reps: 8, weight: 90, unit: 'lbs' }, { reps: 8, weight: 95, unit: 'lbs' }] },
    ],
  }),

  // ── Week 6 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_20',
    title: 'HIIT Full Body',
    workoutType: 'hiit',
    duration: 35,
    caloriesBurned: 400,
    completedAt: subDays(now, 36).toISOString(),
    createdAt: subDays(now, 36).toISOString(),
    exercises: [
      { name: 'Burpee', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }] },
      { name: 'Kettlebell Swing', sets: [{ reps: 20, weight: 35, unit: 'lbs' }, { reps: 20, weight: 35, unit: 'lbs' }] },
      { name: 'Box Jump', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }] },
      { name: 'Mountain Climber', sets: [{ reps: 30, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_21',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 52,
    caloriesBurned: 350,
    completedAt: subDays(now, 38).toISOString(),
    createdAt: subDays(now, 38).toISOString(),
    exercises: [
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 140, unit: 'lbs' }, { reps: 8, weight: 150, unit: 'lbs' }] },
      { name: 'Lat Pulldown', sets: [{ reps: 12, weight: 130, unit: 'lbs' }, { reps: 10, weight: 140, unit: 'lbs' }] },
      { name: 'Hammer Curl', sets: [{ reps: 12, weight: 25, unit: 'lbs' }, { reps: 10, weight: 30, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_22',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 65,
    caloriesBurned: 450,
    completedAt: subDays(now, 40).toISOString(),
    createdAt: subDays(now, 40).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 205, unit: 'lbs' }, { reps: 5, weight: 225, unit: 'lbs' }, { reps: 5, weight: 245, unit: 'lbs' }] },
      { name: 'Romanian Deadlift', sets: [{ reps: 10, weight: 175, unit: 'lbs' }, { reps: 10, weight: 180, unit: 'lbs' }] },
      { name: 'Lunges', sets: [{ reps: 12, weight: 40, unit: 'lbs' }, { reps: 12, weight: 40, unit: 'lbs' }] },
    ],
  }),

  // ── Week 7 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_23',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 58,
    caloriesBurned: 380,
    completedAt: subDays(now, 43).toISOString(),
    createdAt: subDays(now, 43).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 170, unit: 'lbs' }, { reps: 8, weight: 170, unit: 'lbs' }, { reps: 6, weight: 180, unit: 'lbs' }] },
      { name: 'Dumbbell Shoulder Press', sets: [{ reps: 10, weight: 45, unit: 'lbs' }, { reps: 10, weight: 50, unit: 'lbs' }] },
      { name: 'Chest Fly', sets: [{ reps: 12, weight: 25, unit: 'lbs' }, { reps: 12, weight: 25, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_24',
    title: 'Cardio Session',
    workoutType: 'cardio',
    duration: 45,
    caloriesBurned: 420,
    completedAt: subDays(now, 45).toISOString(),
    createdAt: subDays(now, 45).toISOString(),
    exercises: [
      { name: 'Cycling', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      { name: 'Jump Rope', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_25',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 50,
    caloriesBurned: 340,
    completedAt: subDays(now, 47).toISOString(),
    createdAt: subDays(now, 47).toISOString(),
    exercises: [
      { name: 'Deadlift', sets: [{ reps: 5, weight: 275, unit: 'lbs' }, { reps: 5, weight: 295, unit: 'lbs' }, { reps: 3, weight: 325, unit: 'lbs' }] },
      { name: 'Pull-Up', sets: [{ reps: 8, weight: 0, unit: 'lbs' }, { reps: 7, weight: 0, unit: 'lbs' }] },
      { name: 'Cable Curl', sets: [{ reps: 15, weight: 40, unit: 'lbs' }, { reps: 12, weight: 45, unit: 'lbs' }] },
    ],
  }),

  // ── Week 8 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_26',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 63,
    caloriesBurned: 440,
    completedAt: subDays(now, 50).toISOString(),
    createdAt: subDays(now, 50).toISOString(),
    exercises: [
      { name: 'Front Squat', sets: [{ reps: 8, weight: 175, unit: 'lbs' }, { reps: 8, weight: 185, unit: 'lbs' }, { reps: 6, weight: 195, unit: 'lbs' }] },
      { name: 'Hip Thrust', sets: [{ reps: 12, weight: 175, unit: 'lbs' }, { reps: 12, weight: 175, unit: 'lbs' }] },
      { name: 'Leg Extension', sets: [{ reps: 15, weight: 90, unit: 'lbs' }, { reps: 12, weight: 100, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_27',
    title: 'Upper Body',
    workoutType: 'strength',
    duration: 55,
    caloriesBurned: 370,
    completedAt: subDays(now, 52).toISOString(),
    createdAt: subDays(now, 52).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 165, unit: 'lbs' }, { reps: 8, weight: 165, unit: 'lbs' }, { reps: 6, weight: 175, unit: 'lbs' }] },
      { name: 'Overhead Press', sets: [{ reps: 8, weight: 85, unit: 'lbs' }, { reps: 8, weight: 90, unit: 'lbs' }] },
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 135, unit: 'lbs' }, { reps: 8, weight: 145, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_28',
    title: 'Yoga Flow',
    workoutType: 'flexibility',
    duration: 40,
    caloriesBurned: 140,
    completedAt: subDays(now, 54).toISOString(),
    createdAt: subDays(now, 54).toISOString(),
    exercises: [
      { name: 'Yoga Flow', sets: [{ reps: 10, weight: 0, unit: 'lbs' }] },
      { name: 'Dynamic Stretching', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),

  // ── Week 9 ──────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_29',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 56,
    caloriesBurned: 370,
    completedAt: subDays(now, 57).toISOString(),
    createdAt: subDays(now, 57).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 160, unit: 'lbs' }, { reps: 8, weight: 160, unit: 'lbs' }, { reps: 6, weight: 170, unit: 'lbs' }] },
      { name: 'Incline Bench Press', sets: [{ reps: 10, weight: 115, unit: 'lbs' }, { reps: 8, weight: 125, unit: 'lbs' }] },
      { name: 'Lateral Raise', sets: [{ reps: 15, weight: 15, unit: 'lbs' }, { reps: 15, weight: 15, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_30',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 52,
    caloriesBurned: 350,
    completedAt: subDays(now, 59).toISOString(),
    createdAt: subDays(now, 59).toISOString(),
    exercises: [
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 130, unit: 'lbs' }, { reps: 8, weight: 140, unit: 'lbs' }] },
      { name: 'Lat Pulldown', sets: [{ reps: 12, weight: 120, unit: 'lbs' }, { reps: 10, weight: 130, unit: 'lbs' }] },
      { name: 'Preacher Curl', sets: [{ reps: 12, weight: 50, unit: 'lbs' }, { reps: 10, weight: 55, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_31',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 65,
    caloriesBurned: 460,
    completedAt: subDays(now, 61).toISOString(),
    createdAt: subDays(now, 61).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 195, unit: 'lbs' }, { reps: 5, weight: 215, unit: 'lbs' }, { reps: 5, weight: 235, unit: 'lbs' }] },
      { name: 'Leg Press', sets: [{ reps: 12, weight: 320, unit: 'lbs' }, { reps: 10, weight: 360, unit: 'lbs' }] },
      { name: 'Walking Lunge', sets: [{ reps: 12, weight: 35, unit: 'lbs' }, { reps: 12, weight: 35, unit: 'lbs' }] },
    ],
  }),

  // ── Week 10 ─────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_32',
    title: 'Full Body Circuit',
    workoutType: 'hiit',
    duration: 40,
    caloriesBurned: 450,
    completedAt: subDays(now, 64).toISOString(),
    createdAt: subDays(now, 64).toISOString(),
    exercises: [
      { name: 'Thruster', sets: [{ reps: 10, weight: 65, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }] },
      { name: 'Burpee', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }] },
      { name: 'Kettlebell Swing', sets: [{ reps: 15, weight: 35, unit: 'lbs' }, { reps: 15, weight: 35, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_33',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 55,
    caloriesBurned: 360,
    completedAt: subDays(now, 66).toISOString(),
    createdAt: subDays(now, 66).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 155, unit: 'lbs' }, { reps: 8, weight: 155, unit: 'lbs' }, { reps: 6, weight: 165, unit: 'lbs' }] },
      { name: 'Overhead Press', sets: [{ reps: 8, weight: 80, unit: 'lbs' }, { reps: 8, weight: 85, unit: 'lbs' }] },
      { name: 'Dips (Chest)', sets: [{ reps: 10, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_34',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 50,
    caloriesBurned: 335,
    completedAt: subDays(now, 68).toISOString(),
    createdAt: subDays(now, 68).toISOString(),
    exercises: [
      { name: 'Deadlift', sets: [{ reps: 5, weight: 255, unit: 'lbs' }, { reps: 5, weight: 275, unit: 'lbs' }, { reps: 3, weight: 305, unit: 'lbs' }] },
      { name: 'Dumbbell Row', sets: [{ reps: 10, weight: 55, unit: 'lbs' }, { reps: 10, weight: 60, unit: 'lbs' }] },
      { name: 'EZ Bar Curl', sets: [{ reps: 12, weight: 55, unit: 'lbs' }, { reps: 10, weight: 60, unit: 'lbs' }] },
    ],
  }),

  // ── Week 11 ─────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_35',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 62,
    caloriesBurned: 440,
    completedAt: subDays(now, 71).toISOString(),
    createdAt: subDays(now, 71).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 185, unit: 'lbs' }, { reps: 5, weight: 205, unit: 'lbs' }, { reps: 5, weight: 225, unit: 'lbs' }] },
      { name: 'Romanian Deadlift', sets: [{ reps: 10, weight: 165, unit: 'lbs' }, { reps: 10, weight: 170, unit: 'lbs' }] },
      { name: 'Leg Curl', sets: [{ reps: 12, weight: 80, unit: 'lbs' }, { reps: 12, weight: 90, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_36',
    title: 'Morning Cardio',
    workoutType: 'cardio',
    duration: 30,
    caloriesBurned: 280,
    completedAt: subDays(now, 73).toISOString(),
    createdAt: subDays(now, 73).toISOString(),
    exercises: [
      { name: 'Elliptical', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      { name: 'Walking', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_37',
    title: 'Upper Body',
    workoutType: 'strength',
    duration: 50,
    caloriesBurned: 330,
    completedAt: subDays(now, 75).toISOString(),
    createdAt: subDays(now, 75).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 145, unit: 'lbs' }, { reps: 8, weight: 150, unit: 'lbs' }] },
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 125, unit: 'lbs' }, { reps: 8, weight: 135, unit: 'lbs' }] },
      { name: 'Overhead Press', sets: [{ reps: 8, weight: 75, unit: 'lbs' }, { reps: 8, weight: 80, unit: 'lbs' }] },
    ],
  }),

  // ── Week 12 ─────────────────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_38',
    title: 'Push Day',
    workoutType: 'strength',
    duration: 52,
    caloriesBurned: 345,
    completedAt: subDays(now, 78).toISOString(),
    createdAt: subDays(now, 78).toISOString(),
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 8, weight: 140, unit: 'lbs' }, { reps: 8, weight: 145, unit: 'lbs' }] },
      { name: 'Incline Dumbbell Press', sets: [{ reps: 10, weight: 45, unit: 'lbs' }, { reps: 10, weight: 50, unit: 'lbs' }] },
      { name: 'Lateral Raise', sets: [{ reps: 15, weight: 12, unit: 'lbs' }, { reps: 15, weight: 15, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_39',
    title: 'Leg Day',
    workoutType: 'strength',
    duration: 60,
    caloriesBurned: 420,
    completedAt: subDays(now, 80).toISOString(),
    createdAt: subDays(now, 80).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 175, unit: 'lbs' }, { reps: 5, weight: 195, unit: 'lbs' }] },
      { name: 'Leg Press', sets: [{ reps: 12, weight: 300, unit: 'lbs' }, { reps: 10, weight: 340, unit: 'lbs' }] },
      { name: 'Calf Raise', sets: [{ reps: 20, weight: 120, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_40',
    title: 'Pull Day',
    workoutType: 'strength',
    duration: 48,
    caloriesBurned: 320,
    completedAt: subDays(now, 82).toISOString(),
    createdAt: subDays(now, 82).toISOString(),
    exercises: [
      { name: 'Deadlift', sets: [{ reps: 5, weight: 235, unit: 'lbs' }, { reps: 5, weight: 255, unit: 'lbs' }] },
      { name: 'Chin-Up', sets: [{ reps: 8, weight: 0, unit: 'lbs' }, { reps: 6, weight: 0, unit: 'lbs' }] },
      { name: 'Barbell Curl', sets: [{ reps: 12, weight: 50, unit: 'lbs' }, { reps: 10, weight: 55, unit: 'lbs' }] },
    ],
  }),

  // ── Week 13 (oldest) ───────────────────────────────────────────────────
  makeWorkout({
    id: 'mock_wkt_41',
    title: 'Full Body Intro',
    workoutType: 'strength',
    duration: 45,
    caloriesBurned: 300,
    completedAt: subDays(now, 85).toISOString(),
    createdAt: subDays(now, 85).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 8, weight: 165, unit: 'lbs' }, { reps: 8, weight: 175, unit: 'lbs' }] },
      { name: 'Bench Press', sets: [{ reps: 8, weight: 135, unit: 'lbs' }, { reps: 8, weight: 135, unit: 'lbs' }] },
      { name: 'Barbell Row', sets: [{ reps: 10, weight: 115, unit: 'lbs' }, { reps: 8, weight: 125, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_42',
    title: 'Cardio & Abs',
    workoutType: 'cardio',
    duration: 35,
    caloriesBurned: 300,
    completedAt: subDays(now, 87).toISOString(),
    createdAt: subDays(now, 87).toISOString(),
    exercises: [
      { name: 'Treadmill', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      { name: 'Crunch', sets: [{ reps: 20, weight: 0, unit: 'lbs' }, { reps: 20, weight: 0, unit: 'lbs' }] },
      { name: 'Plank', sets: [{ reps: 3, weight: 0, unit: 'lbs' }] },
    ],
  }),
  makeWorkout({
    id: 'mock_wkt_43',
    title: 'Starting Strength Day 1',
    workoutType: 'strength',
    duration: 40,
    caloriesBurned: 260,
    completedAt: subDays(now, 90).toISOString(),
    createdAt: subDays(now, 90).toISOString(),
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 155, unit: 'lbs' }, { reps: 5, weight: 155, unit: 'lbs' }] },
      { name: 'Bench Press', sets: [{ reps: 5, weight: 125, unit: 'lbs' }, { reps: 5, weight: 125, unit: 'lbs' }] },
      { name: 'Deadlift', sets: [{ reps: 5, weight: 225, unit: 'lbs' }] },
    ],
  }),
];

/**
 * Workout templates.
 */
export const MOCK_TEMPLATES = [
  makeWorkout({
    id: 'mock_tpl_01',
    title: 'Push Pull Legs - Push',
    workoutType: 'strength',
    isTemplate: true,
    duration: 0,
    caloriesBurned: 0,
    exercises: [
      { name: 'Bench Press', sets: [{ reps: 10, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }, { reps: 6, weight: 0, unit: 'lbs' }], notes: '', restTime: 90 },
      { name: 'Overhead Press', sets: [{ reps: 10, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }], notes: '', restTime: 90 },
      { name: 'Incline Dumbbell Press', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }], notes: '', restTime: 60 },
      { name: 'Lateral Raise', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }], notes: '', restTime: 45 },
      { name: 'Tricep Pushdown', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }], notes: '', restTime: 45 },
    ],
  }),
  makeWorkout({
    id: 'mock_tpl_02',
    title: '5x5 Stronglifts',
    workoutType: 'strength',
    isTemplate: true,
    duration: 0,
    caloriesBurned: 0,
    exercises: [
      { name: 'Squat', sets: [{ reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }], notes: '', restTime: 180 },
      { name: 'Bench Press', sets: [{ reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }], notes: '', restTime: 180 },
      { name: 'Barbell Row', sets: [{ reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }], notes: '', restTime: 180 },
    ],
  }),
  makeWorkout({
    id: 'mock_tpl_03',
    title: 'Morning Cardio',
    workoutType: 'cardio',
    isTemplate: true,
    duration: 0,
    caloriesBurned: 0,
    exercises: [
      { name: 'Treadmill', sets: [{ reps: 1, weight: 0, unit: 'lbs' }], notes: '5 min easy pace', restTime: 0 },
      { name: 'Walking', sets: [{ reps: 1, weight: 0, unit: 'lbs' }], notes: '20 min at 3.5mph 12% incline', restTime: 0 },
      { name: 'Stair Climber', sets: [{ reps: 1, weight: 0, unit: 'lbs' }], notes: '10 min moderate pace', restTime: 0 },
      { name: 'Walking', sets: [{ reps: 1, weight: 0, unit: 'lbs' }], notes: '5 min easy', restTime: 0 },
    ],
  }),
  makeWorkout({
    id: 'mock_tpl_04',
    title: 'Full Body Circuit',
    workoutType: 'hiit',
    isTemplate: true,
    duration: 0,
    caloriesBurned: 0,
    exercises: [
      { name: 'Burpee', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }], notes: '', restTime: 30 },
      { name: 'Thruster', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }], notes: '', restTime: 30 },
      { name: 'Box Jump', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }], notes: '', restTime: 30 },
      { name: 'Kettlebell Swing', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }], notes: '', restTime: 30 },
      { name: 'Mountain Climber', sets: [{ reps: 30, weight: 0, unit: 'lbs' }, { reps: 30, weight: 0, unit: 'lbs' }], notes: '', restTime: 30 },
    ],
  }),
];

/**
 * Pre-computed mock workout stats for the current user.
 */
export const MOCK_WORKOUT_STATS = {
  totalWorkouts: MOCK_WORKOUTS.length,
  totalDuration: MOCK_WORKOUTS.reduce((sum, w) => sum + w.duration, 0),
  thisWeekCount: 3,
  currentStreak: 3,
  avgDuration: Math.round(MOCK_WORKOUTS.reduce((sum, w) => sum + w.duration, 0) / MOCK_WORKOUTS.length),
};
